
import React, { useState, useEffect } from 'react';
import { FaSearch, FaCheck } from 'react-icons/fa';
import BotaoPadrao from '../BotaoPadrao/BotaoPadrao';
import BotaoAdicionarVinculos from '../BotaoAdicionarVinculos';
import InputPadrao from '../InputPadrao/InputPadrao';
import Carregamento from '../Carregamento/Carregamento';
import './ModalFuncionalidadeAdicional.css';
import TabelaPadraoDividida from '../TabelaPadraoDividida/TabelaPadraoDividida';
import api from '../../services/api';
import NotificacaoUtils from '../../utils/NotificacaoUtils';
import { FuncionalidadesAdicionais } from '../../utils/EnumsUtils';
import Tooltip from '../Tooltip/Tooltip';
import { EnumSistemas } from '../CadastrarSistemaModal/CadastrarSistemaModal';

export function mapearDescricaoParaEnum(descricao) {
  if (!descricao) return descricao;
  const descricaoNormalizada = descricao.toLowerCase().replace(/\s+/g, '');
  for (const [, valor] of Object.entries(FuncionalidadesAdicionais)) {
    const enumNormalizado = valor.toLowerCase().replace(/\s+/g, '');
    if (descricaoNormalizada.includes(enumNormalizado) || enumNormalizado.includes(descricaoNormalizada)) {
      return valor;
    }
  }
  return descricao;
}

export function formatarItem(item, idx) {
  const descricaoOriginal = item.descricaoFuncionalidadeAdicional || item.descricao || '';
  const descricaoMapeada = mapearDescricaoParaEnum(descricaoOriginal);
  return {
    id: item.idFuncionalidadeAdicionalTipoSistema || idx + 1,
    idFuncionalidadeAdicionalTipoSistema: item.idFuncionalidadeAdicionalTipoSistema,
    funcionalidade: descricaoMapeada,
    nome: descricaoMapeada,
    descricaoOriginal,
    ...item
  };
}

const TAMANHO_PAGINA = 8;

export default function ModalFuncionalidadeAdicional({
  onCancelar,
  onSalvar,
  unidadeNegocio,
  plano,
  tipoSistema,
  idSistema,
  funcionalidadesSelecionadas = []
}) {
  const [filtroNome, setFiltroNome] = useState('');
  const [loadingBusca, setLoadingBusca] = useState(false);

  // Lista completa vinda do backend — paginação é feita localmente
  const [funcionalidades, setFuncionalidades] = useState([]);
  const [itensFiltrados, setItensFiltrados] = useState([]);

  const [selecionados, setSelecionados] = useState(funcionalidadesSelecionadas);
  const [funcionalidadesOriginais, setFuncionalidadesOriginais] = useState([]);

  const [paginaEsquerda, setPaginaEsquerda] = useState(1);
  const [paginaDireita, setPaginaDireita] = useState(1);

  // Carrega catálogo completo do backend
  useEffect(() => {
    const carregarFuncionalidades = async () => {
      try {
        setLoadingBusca(true);

        let idTipoSistema = null;
        if (tipoSistema) {
          const licencaNome = plano?.descricao || plano?.nome || '';
          const tipoStr = String(tipoSistema).toLowerCase();
          if (
            tipoStr.includes(EnumSistemas.DIXI_PONTO.toLowerCase()) ||
            licencaNome.toLowerCase().includes(EnumSistemas.DIXI_PONTO.toLowerCase())
          ) {
            idTipoSistema = 1;
          } else if (
            tipoStr.includes(EnumSistemas.DIXI_ACESSOS.toLowerCase()) ||
            licencaNome.toLowerCase().includes(EnumSistemas.DIXI_ACESSOS.toLowerCase())
          ) {
            idTipoSistema = 2;
          }
        }

        const url = idTipoSistema
          ? `/funcionalidadeAdicional_?idTipoSistema=${idTipoSistema}`
          : '/funcionalidadeAdicional_';

        const resposta = await api.get(url);

        // Suporta tanto array direto quanto objeto paginado { content: [] }
        const dados = Array.isArray(resposta?.data)
          ? resposta.data
          : Array.isArray(resposta?.data?.content)
            ? resposta.data.content
            : [];

        const formatados = dados.map(formatarItem);
        setFuncionalidades(formatados);
        setItensFiltrados(formatados);
      } catch {
        NotificacaoUtils.erro('Erro ao carregar funcionalidades adicionais');
      } finally {
        setLoadingBusca(false);
      }
    };

    carregarFuncionalidades();
  }, [tipoSistema, plano]);

  // Carrega funcionalidades já vinculadas ao sistema
  useEffect(() => {
    if (!idSistema) return;

    const carregarVinculadas = async () => {
      try {
        const resposta = await api.get(`/funcionalidadeAdicional_/all/${idSistema}`);

        if (resposta?.data && Array.isArray(resposta.data)) {
          const vinculadas = resposta.data
            .map((item, idx) => {
              const descricaoOriginal =
                item.funcionalidadeTipoSistema?.funcionalidade?.descricao ||
                item.funcionalidadeTipoSistema?.descricaoFuncionalidadeAdicional ||
                item.descricaoFuncionalidadeAdicional ||
                item.descricao ||
                item.nome ||
                '';
              const descricaoMapeada = mapearDescricaoParaEnum(descricaoOriginal);
              return {
                id: item.funcionalidadeTipoSistema?.idFuncionalidadeTipoSistema || item.id || idx + 1,
                idFuncionalidadeTipoSistema: item.funcionalidadeTipoSistema?.idFuncionalidadeTipoSistema,
                funcionalidade: descricaoMapeada,
                nome: descricaoMapeada,
                descricaoOriginal,
                ativo: item.ativo,
                ...item
              };
            })
            .filter(f => f.ativo !== false);

          setSelecionados(vinculadas);
          setFuncionalidadesOriginais(vinculadas.map(f => f.idFuncionalidadeTipoSistema));
        }
      } catch {
        // Não bloqueia o uso do modal
      }
    };

    carregarVinculadas();
  }, [idSistema]);

  const handlePesquisar = () => {
    setLoadingBusca(true);
    setTimeout(() => {
      const filtro = filtroNome.trim().toLowerCase();
      setItensFiltrados(
        filtro
          ? funcionalidades.filter(f =>
              (f.nome || f.funcionalidade || '').toLowerCase().startsWith(filtro)
            )
          : funcionalidades
      );
      setPaginaEsquerda(1);
      setLoadingBusca(false);
    }, 300);
  };

  const resolverIdSelecionado = (s) =>
    s.idFuncionalidadeTipoSistema ||
    s.funcionalidadeTipoSistema?.idFuncionalidadeTipoSistema ||
    s.idFuncionalidadeAdicionalTipoSistema;

  // Exclui da lista esquerda os itens já selecionados
  const catalogoDisponivel = itensFiltrados.filter(item => {
    const idEsquerda = item.idFuncionalidadeAdicionalTipoSistema;
    return !selecionados.some(s => resolverIdSelecionado(s) === idEsquerda);
  });

  const adicionarFuncionalidade = (item) => {
    const idEsquerda = item.idFuncionalidadeAdicionalTipoSistema;
    const jaAdicionado = selecionados.some(s => resolverIdSelecionado(s) === idEsquerda);
    if (!jaAdicionado) {
      setSelecionados(prev => [...prev, { ...item, idFuncionalidadeTipoSistema: item.idFuncionalidadeAdicionalTipoSistema }]);
    }
  };

  const adicionarTodasFuncionalidades = () => {
    const novos = catalogoDisponivel.map(item => ({
      ...item,
      idFuncionalidadeTipoSistema: item.idFuncionalidadeAdicionalTipoSistema
    }));
    setSelecionados(prev => [...prev, ...novos]);
    setPaginaEsquerda(1);
  };

  const removerFuncionalidade = (item) => {
    setSelecionados(prev => prev.filter(f => f.id !== item.id));
  };

  const removerTodasFuncionalidades = () => {
    setSelecionados([]);
    setPaginaDireita(1);
  };

  const handleSalvarVinculos = () => {
    const idsSelecionados = selecionados
      .map(f =>
        f.idFuncionalidadeTipoSistema ||
        f.funcionalidadeTipoSistema?.idFuncionalidadeTipoSistema ||
        f.idFuncionalidadeAdicionalTipoSistema
      )
      .filter(Boolean);

    const idsAdicionar = idsSelecionados.filter(id => !funcionalidadesOriginais.includes(id));
    const idsRemover = funcionalidadesOriginais.filter(id => !idsSelecionados.includes(id));

    if (onSalvar) {
      onSalvar({
        idsFuncionalidadesAdicionar: idsAdicionar,
        idsFuncionalidadesRemover: idsRemover,
        funcionalidadesSelecionadas: selecionados
      });
    }

    if (onCancelar) onCancelar();
  };

  const colunas = [{ campo: 'funcionalidade', titulo: 'Funcionalidade' }];

  // Paginação local — tabela esquerda
  const totalPaginasEsquerda = Math.ceil(catalogoDisponivel.length / TAMANHO_PAGINA) || 1;
  const paginadosEsquerda = catalogoDisponivel.slice(
    (paginaEsquerda - 1) * TAMANHO_PAGINA,
    paginaEsquerda * TAMANHO_PAGINA
  );

  // Paginação local — tabela direita
  const totalPaginasDireita = Math.ceil(selecionados.length / TAMANHO_PAGINA) || 1;
  const paginadosDireita = selecionados.slice(
    (paginaDireita - 1) * TAMANHO_PAGINA,
    paginaDireita * TAMANHO_PAGINA
  );

  return (
    <div className="modal-funcionalidade-adicional-container">
      {loadingBusca && (
        <div className="modal-carregamento-overlay">
          <Carregamento />
        </div>
      )}

      <div className="modal-busca-filtros">
        <div className="modal-busca-cabecalho-funcionalidade">
          <div className="sistema-info-container">
            <div className="sistema-info">
              <span style={{ fontWeight: 'bold' }}>Licença: </span>
              <span>{plano?.descricao}{tipoSistema ? `${tipoSistema}` : ''}</span>
            </div>
            <div className="sistema-info">
              <span style={{ fontWeight: 'bold' }}>Unidade: </span>
              <span>{unidadeNegocio}</span>
            </div>
          </div>
        </div>

        <div className="modal-busca-campo-pesquisa">
          <InputPadrao
            label="Nome"
            value={filtroNome}
            onChange={e => setFiltroNome(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handlePesquisar()}
            placeholder="Pesquise"
          />
          <div className="search-button-container">
            <BotaoPadrao
              aoClicar={handlePesquisar}
              icone={<FaSearch />}
              confirmar={true}
              classeCustom="botao-pesquisar pequeno"
            />
          </div>
        </div>
      </div>

      <div className="modal-busca-conteudo">
        <TabelaPadraoDividida
          classCustom="modal-funcionalidade-tabelas-custom"
          tabelaEsquerda={{
            titulo: 'Lista de Funções',
            dados: paginadosEsquerda,
            colunas,
            totalRegistros: catalogoDisponivel.length,
            paginaAtual: paginaEsquerda - 1,
            tamanhoPagina: TAMANHO_PAGINA,
            paginaAnterior: () => setPaginaEsquerda(p => Math.max(1, p - 1)),
            proximaPagina: () => setPaginaEsquerda(p => Math.min(totalPaginasEsquerda, p + 1)),
            irParaPagina: (pagina) => setPaginaEsquerda(Math.max(1, Math.min(totalPaginasEsquerda, pagina + 1))),
            onAcao: adicionarFuncionalidade,
            acaoPersonalizada: (item) => (
              <BotaoAdicionarVinculos
                adicionar={true}
                onClick={e => {
                  e?.stopPropagation();
                  adicionarFuncionalidade(item);
                }}
              />
            ),
            acaoCabecalho: (
              <Tooltip text="Adicionar todos">
                <BotaoAdicionarVinculos adicionar={true} onClick={adicionarTodasFuncionalidades} />
              </Tooltip>
            ),
          }}
          tabelaDireita={{
            titulo: 'Funções Adicionadas',
            dados: paginadosDireita,
            colunas,
            totalRegistros: selecionados.length,
            paginaAtual: paginaDireita - 1,
            tamanhoPagina: TAMANHO_PAGINA,
            paginaAnterior: () => setPaginaDireita(p => Math.max(1, p - 1)),
            proximaPagina: () => setPaginaDireita(p => Math.min(totalPaginasDireita, p + 1)),
            irParaPagina: (pagina) => setPaginaDireita(Math.max(1, Math.min(totalPaginasDireita, pagina + 1))),
            onAcao: removerFuncionalidade,
            acaoCabecalho: (
              <Tooltip text="Remover todos">
                <BotaoAdicionarVinculos adicionar={false} onClick={removerTodasFuncionalidades} />
              </Tooltip>
            ),
          }}
          exibeAcao={false}
          modeloNovo={true}
        />
      </div>

      <div className="modal-busca-botoes">
        <BotaoPadrao cancelar={true} aoClicar={onCancelar} texto="Cancelar" />
        <BotaoPadrao icone={<FaCheck />} confirmar={true} aoClicar={handleSalvarVinculos} texto="Confirmar" />
      </div>
    </div>
  );
}
